import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { usePictureRequestAmount } from "@/api/usePictureRequestAmount";
import { useApiClient } from "@/hooks/useApiClient";
import { useFeatureFlags } from "@/configs/featureFlag";
import { UserConfigGetAPI } from "@/features/accountSettings/api/UserConfigAPI";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { ALL_MATTERS_FIELDS } from "../utils/checkboxFields";
import { type BulkAcquisitionBreakdown } from "../types";
import { formatNumberWithComma } from "@/utils/formatNumber";

// 見出しアンカー（#以降）は Notion 側の文言・章立て変更で無効になりエラー画面になるため、ページまでに留める
const MANUAL_LINK_URL =
  "https://trustart.notion.site/4-1d114754f2df806f9991e47fdee087bd";

interface ConfirmBulkAcquisitionDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  /** チェックが1件以上ある取得対象の列 field 名（kyoutan / ownerInfo 等） */
  checkedFields: string[];
  /** 課金件数は金額の表示可否に関わらず必ず知らせるため、単価とは別に描画する */
  breakdown: BulkAcquisitionBreakdown | null;
  /**
   * 確定押下時に1回だけ呼ばれる。
   * 失敗時に再操作可能へ戻すには cancelLoading を呼ぶ
   */
  onConfirm: (cancelLoading: () => void) => void;
}

/**
 * 一括取得実行前の確認モーダル（課金前の必須確認）。
 * 表示は既存 /acquiremultiplebooks の ConfirmAcquireMultipleBooksDialog と同構成で、
 * チェック済みの取得対象の単価のみを列挙する
 */
export const ConfirmBulkAcquisitionDialog: React.FC<
  ConfirmBulkAcquisitionDialogProps
> = ({ open, setOpen, checkedFields, breakdown, onConfirm }) => {
  const userConfig = UserConfigGetAPI();
  const { displayChargePriceInRequestModal } = useFeatureFlags();
  const { apiClient } = useApiClient();
  const { data: pictureRequestAmount } = usePictureRequestAmount(apiClient);
  const isReadOnly = useReadOnlyUser();

  // 送信中はキャンセル・閉じる操作を無効にする
  // （送信中に閉じて開き直すと2本目の依頼が送れてしまうため）
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (open) {
      setSubmitting(false);
    }
  }, [open]);

  const isLoadedPictureRequestAmount =
    pictureRequestAmount?.shoyushajiko != null &&
    pictureRequestAmount.zenbujiko != null &&
    pictureRequestAmount.chisekisokuryozu != null &&
    pictureRequestAmount.denshikozu != null &&
    pictureRequestAmount.chiekikenzumen != null &&
    pictureRequestAmount.tatemonozumen != null;

  // 単価はフィーチャーフラグ・金額の取得完了・ユーザーの価格表示設定で出し分ける。
  // 書類件数はこの条件に関わらず常に出す（課金前に件数を知らせないままにしない）
  const canDisplayPrice =
    displayChargePriceInRequestModal === true &&
    isLoadedPictureRequestAmount &&
    userConfig !== undefined &&
    !userConfig.isPriceHidden;

  const isCheckedSome = (fields: string[]): boolean =>
    fields.some((field) => checkedFields.includes(field));

  const priceRows = [
    {
      fields: ["ownerInfo"],
      label: "所有者事項",
      amount: pictureRequestAmount?.shoyushajiko,
    },
    {
      fields: [...ALL_MATTERS_FIELDS],
      label: "全部事項",
      amount: pictureRequestAmount?.zenbujiko,
    },
    {
      fields: ["tisekiDrawings"],
      label: "地積測量図",
      amount: pictureRequestAmount?.chisekisokuryozu,
    },
    {
      fields: ["electricDrawings"],
      label: "電子公図",
      amount: pictureRequestAmount?.denshikozu,
    },
    {
      fields: ["tiekiDrawings"],
      label: "地役権図面",
      amount: pictureRequestAmount?.chiekikenzumen,
    },
    {
      fields: ["buildingDrawings"],
      label: "建物図面/各階平面図",
      amount: pictureRequestAmount?.tatemonozumen,
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // 誤操作で課金前の確認を閉じないよう、外クリック・Escでは閉じない
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        setOpen(false);
      }}
      aria-labelledby="bulk-confirm-dialog-title"
      aria-describedby="bulk-confirm-dialog-description"
    >
      <DialogTitle id="bulk-confirm-dialog-title">{"確認画面"}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div" id="bulk-confirm-dialog-description">
          {canDisplayPrice ? (
            <>
              <Typography>登記/図面の取得を開始します。</Typography>
              <Typography>
                取得件数に対して、1件あたり以下の料金が発生します。
              </Typography>
              <Box component="ul" sx={{ pl: 5, my: 2 }}>
                {priceRows.map(
                  (row) =>
                    isCheckedSome(row.fields) && (
                      <Typography component="li" key={row.label}>
                        {row.label}：{formatNumberWithComma(row.amount)}円
                      </Typography>
                    )
                )}
              </Box>
              <Typography>
                ※取得済登記・リユース登記となる場合は価格が異なります。
              </Typography>
            </>
          ) : (
            <>
              <Typography>
                【課金が発生します】登記/図面を取得します。
              </Typography>
              <Typography>
                ※ 画面の切り替わりまでに時間がかかる場合があります
              </Typography>
            </>
          )}
          {breakdown != null && (
            <>
              {breakdown.hiddenPictureCount > 0 && (
                <Alert severity="warning" sx={{ my: 2 }}>
                  絞り込みで非表示になっている行の書類
                  {breakdown.hiddenPictureCount.toLocaleString()}
                  件を含みます。
                </Alert>
              )}
              <TableContainer component={Paper} sx={{ my: 2 }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "whitesmoke" }}>
                    <TableRow>
                      <TableCell>取得方法</TableCell>
                      <TableCell align="right">書類件数</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>新規取得</TableCell>
                      <TableCell align="right">
                        {breakdown.newPictureCount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>取得済登記</TableCell>
                      <TableCell align="right">
                        {breakdown.acquiredPictureCount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>リユース登記</TableCell>
                      <TableCell align="right">
                        {breakdown.reusePictureCount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
          {canDisplayPrice && (
            <Typography>
              詳細は
              <Link
                href={MANUAL_LINK_URL}
                target="_blank"
                rel="noreferrer"
                sx={{ mx: 0.5 }}
              >
                マニュアル
              </Link>
              をご確認ください。
            </Typography>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", mb: 1 }}>
        <Button
          variant={ButtonVariantOption.Outlined}
          disabled={submitting}
          onClick={() => {
            setOpen(false);
          }}
          label={"キャンセル"}
        />
        <ReadOnlyTooltip isReadOnly={isReadOnly}>
          <SubmitButton
            variant={ButtonVariantOption.Contained}
            onClick={(_event, cancelLoading) => {
              setSubmitting(true);
              onConfirm(() => {
                setSubmitting(false);
                cancelLoading();
              });
            }}
            label={"取得する"}
            timeout={120000}
            onTimeout={() => {
              setSubmitting(false);
            }}
            disabled={isReadOnly}
          />
        </ReadOnlyTooltip>
      </DialogActions>
    </Dialog>
  );
};
